/**
 * Menu Configuration
 *
 * Defines the sidebar navigation menu structure.
 * Menu items are gated by the dynamic RBAC permission keys (`permKey`) in
 * Sidebar.tsx — see the dynamic permission store (usePermissionStore).
 */

import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Activity,
  UserPlus,
  Settings,
  User,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

export interface MenuItem {
  /** Unique key for the menu item */
  key: string;
  /** Display label */
  label: string;
  /** Route path */
  path: string;
  /** Icon component */
  icon: LucideIcon;
  /**
   * Dynamic RBAC permission key. When set, the item is hidden unless the
   * signed-in user holds this key (see Sidebar gating). Items without a
   * `permKey` are visible to any authenticated user.
   */
  permKey?: string;
  /**
   * Invert the loading posture: hide until the grant is confirmed (avoids
   * show-then-vanish flicker for admin-only entries). Default entries fail
   * OPEN until permissions load.
   */
  failClosed?: boolean;
  /** Whether this is a child of another menu */
  parent?: string;
}

export interface MenuGroup {
  /** Unique key for the group */
  key: string;
  /** Display label for the group */
  label: string;
  /** Menu items in this group */
  items: MenuItem[];
}

/**
 * Main navigation menu items
 */
export const MENU_ITEMS: MenuItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    key: 'organizations',
    label: 'Organizations',
    path: '/organizations',
    icon: Building2,
    permKey: 'organization.organization.view',
  },
  {
    key: 'pending-registrations',
    label: 'Pending Registrations',
    path: '/pending-registrations',
    icon: UserPlus,
    permKey: 'organization.organization.registration',
  },
  {
    key: 'users',
    label: 'Platform Users',
    path: '/users',
    icon: Users,
    permKey: 'orgUser.user.view',
  },
  {
    key: 'subscriptions',
    label: 'Subscriptions',
    path: '/subscriptions',
    icon: CreditCard,
    permKey: 'orgPlan.plan.view',
  },
  {
    key: 'audit',
    label: 'Login Audit',
    path: '/audit',
    icon: Activity,
    permKey: 'audit.audit.view',
  },
  {
    key: 'roles',
    label: 'Roles & Permissions',
    path: '/roles',
    icon: ShieldCheck,
    // Dynamic gate: only holders of the role-view permission see this entry.
    // failClosed so it stays hidden until the grant is confirmed.
    permKey: 'userMgmt.role.view',
    failClosed: true,
  },
];

/**
 * User menu items (bottom of sidebar)
 */
export const USER_MENU_ITEMS: MenuItem[] = [
  {
    key: 'profile',
    label: 'Profile',
    path: '/profile',
    icon: User,
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: Settings,
  },
];
